import { useState } from "react";
import { Avatar } from "./Avatar";
import { jarHue } from "./Jar";
import type { Bucket, Child } from "../lib/api";
import { formatCents } from "../lib/money";

/* The parent's page for one child, as a bank passbook.
 *
 * The twelve cards this replaced were all the same weight, so the page gave
 * a parent no way to tell "set this up once in March" from "this week never
 * got paid". A passbook does: a stub stating the account, then ruled sections
 * for money, setup and record.
 */

/* The stub.
 *
 * Answers the only question a parent opens this page with -- is this kid
 * paid up, and how much have they got -- before any scrolling. The stamp is
 * the page's one loud element; everything below it is deliberately quiet.
 */
export function Stub({
  child,
  buckets,
  scheduleLine,
  stamp,
}: {
  child: Child;
  buckets: Bucket[];
  scheduleLine: string;
  stamp: { label: string; sub: string; tone: "paid" | "owing" | "none" };
}) {
  const ink =
    stamp.tone === "owing"
      ? "var(--stamp)"
      : stamp.tone === "paid"
        ? "var(--stamp-ok)"
        : "var(--ink-3)";

  return (
    <div className="stub">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <Avatar name={child.avatar} size={52} ring={child.color} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 26, lineHeight: 1.1 }}>{child.displayName}</h1>
          <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--ink-3)" }}>
            Set up for {child.presetLabel}
          </p>
        </div>
        <div style={{ color: ink, flex: "0 0 auto", marginTop: 2 }}>
          <div className="stamp">
            {stamp.label}
            <br />
            {stamp.sub}
          </div>
        </div>
      </div>

      {/* The four jars as a teller's column of figures, in the numeral face so
          they line up. This is the balance breakdown a parent would otherwise
          scroll three cards to find. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.max(buckets.length, 1)}, 1fr)`,
          gap: 8,
          marginTop: 18,
          paddingTop: 14,
          borderTop: "2px solid var(--paper-edge)",
        }}
      >
        {buckets.map((b) => (
          <div key={b.id} style={{ minWidth: 0 }}>
            <span
              style={{
                display: "block",
                fontFamily: "var(--font-num)",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: jarHue(b.kind).deep,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {b.name}
            </span>
            <span
              className="money"
              style={{ fontSize: 15, fontWeight: 700, display: "block" }}
            >
              {formatCents(b.balanceCents)}
            </span>
          </div>
        ))}
      </div>

      <p
        style={{
          margin: "14px 0 0",
          fontSize: 13.5,
          color: "var(--ink-2)",
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span>
          <span className="money" style={{ fontWeight: 700, color: "var(--ink)" }}>
            {formatCents(child.balances.bankCents)}
          </span>{" "}
          in the bank
        </span>
        {child.balances.walletCents > 0 && (
          <span>
            &middot;{" "}
            <span className="money">{formatCents(child.balances.walletCents)}</span> in
            pocket
          </span>
        )}
        <span style={{ width: "100%", color: "var(--ink-3)" }}>{scheduleLine}</span>
      </p>
    </div>
  );
}

/** A ledger column heading. Not numbered: these are categories, not a sequence. */
export function Section({ label }: { label: string }) {
  return <h2 className="eyebrow">{label}</h2>;
}

/* One line of the book.
 *
 * `value` is what makes this work: closed, the row still states where the
 * setting stands, so a parent can read the whole of Setup without opening
 * anything. `alert` pulls a line out of the run when it needs a person.
 */
export function Line({
  label,
  value,
  alert,
  children,
  defaultOpen = false,
}: {
  label: string;
  value?: string;
  alert?: boolean;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <button
        className="line"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span style={{ flex: 1, fontSize: 16 }}>{label}</span>
        {value && (
          <span
            className="money"
            style={{
              fontSize: 13,
              fontWeight: alert ? 700 : 400,
              color: alert ? "var(--stamp)" : "var(--ink-3)",
              textAlign: "right",
            }}
          >
            {value}
          </span>
        )}
        <svg
          className="line-caret"
          width="10"
          height="14"
          viewBox="0 0 10 14"
          aria-hidden="true"
        >
          <path
            d="M2 1l6 6-6 6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          style={{
            padding: "4px 4px 22px",
            borderBottom: "1px solid var(--paper-edge)",
          }}
        >
          {children}
        </div>
      )}
    </>
  );
}

/** The sheet a group of lines sits on. */
export function Leaf({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--paper)",
        borderRadius: "var(--r-lg)",
        boxShadow: "var(--lift-1)",
        padding: "2px 14px",
      }}
    >
      {children}
    </div>
  );
}
